import React, { useState, useEffect, useContext, useRef } from "react";
import {
  Container,
  Box,
  Typography,
  useMediaQuery,
} from "@mui/material";
import * as Web3Manager from "../../support/Web3Manager";
import * as Common from "../../support/Common";
import * as Constants from "../../support/Constants";
import moment from "moment";
import { useNotification } from "../../hooks/useNotification";
import CompromisosContext from "../../context/compromisos/CompromisosContext";
import SociosContext from "../../context/socios/SociosContext";
import SpinnerContext from "../../context/spinner/SpinnerContext";
import { getCollaborations } from "../../services/CollaborationsServices";
import { getCollaborationCategories } from "../../services/ParametersServices";
import { getPrinciples } from "../../services/PrinciplesServices";
import { ColaboracionesTable } from "../../components/colaboraciones/ColaboracionesTable";
import { ColaboracionesCard } from "../../components/colaboraciones/ColaboracionesCard";
import { useQueryFilters } from "../../hooks/useQueryFilters";

const ColaboracionesPage = () => {
  const matches = useMediaQuery((theme) => theme.breakpoints.down("sm"));

  const pathname = window.location.pathname;

  const { handleOpenNotificacionError, handleOpenNotificacionTx } =
    useNotification();

  const defaultFilters = {
    titulo: "",
    tipo: 0,
    categoria: "TODAS",
    estado: 0,
    emisor: "",
    receptor: "",
    fechaDesde: moment().startOf("day").subtract(1, "year"),
    fechaHasta: moment().endOf("day"),
  };

  const [modeEdit, setModeEdit] = useState(false);
  const [targetPrincipios, setTargetPrincipios] = useState([]);

  const [categoriasColaboracion, setCategoriasColaboracion] = useState([]);
  const [colaboracionesData, setColaboracionesData] = useState([]);
  const [colaboracionSelected, setColaboracionSelected] = useState(null);
  const [comentariosColaboracion, setComentariosColaboracion] = useState([]);
  const [openModalColaboracion, setOpenModalColaboracion] = useState(false);
  const [openModalIniciar, setOpenModalIniciar] = useState(false);
  const [openModalTrabajar, setOpenModalTrabajar] = useState(false);
  const [openModalFinalizar, setOpenModalFinalizar] = useState(false);
  const [openModalReporte, setOpenModalReporte] = useState(false);
  const [loading, setLoading] = useState(false);

  const { showSpinner, openSpinner, closeSpinner } = useContext(SpinnerContext);

  const { compromisos, getCompromisosEnabled } = useContext(CompromisosContext);

  const { socios, getSocios } = useContext(SociosContext);

  const { filtros, setFiltros, syncUrl, initialized } = useQueryFilters(defaultFilters);

  const fetchColaboraciones = async () => {
    const {
      titulo, tipo, categoria,
      estado, emisor, receptor,
      fechaDesde, fechaHasta
    } = filtros;

    const categoriaParsed = categoria === "TODAS" ? "" : categoria;
    setLoading(true);
    try {
      const resp = await getCollaborations(
        emisor,
        receptor,
        titulo,
        categoriaParsed,
        tipo,
        estado,
        fechaDesde.format("YYYY-MM-DD HH:mm:ss"),
        fechaHasta.format("YYYY-MM-DD HH:mm:ss")
      );
      setColaboracionesData(resp);
    } catch (err) {
      handleOpenNotificacionError(Web3Manager.processErrorResponse(err));
    } finally {
      setLoading(false);
    }
  };

  const onSearch = async () => {
    syncUrl(filtros);
    await fetchColaboraciones();
  };

  const handleAgregarColaboracion = () => {
    setOpenModalColaboracion(true);
    setModeEdit(false);
    setColaboracionSelected(null);
  };

  const handleEditarColaboracion = (colaboracionSelected) => {
    setOpenModalColaboracion(true);
    setModeEdit(true);
    setColaboracionSelected(colaboracionSelected);
    setComentariosColaboracion(colaboracionSelected.comments);
  };

  const handleIniciarColaboracion = (colaboracionSelected) => {
    setOpenModalIniciar(true);
    setModeEdit(false);
    const colaboracionConTituloTipo = {
      ...colaboracionSelected,
      tipoDeColaboracionTitulo:
        obtenerTituloDeTipoColaboracionSelected(colaboracionSelected),
    };
    setColaboracionSelected(colaboracionConTituloTipo);
  };

  const obtenerTituloDeTipoColaboracionSelected = (colaboracionSelected) => {
    if (colaboracionSelected) {
      const code = colaboracionSelected.collaborationTypeCode;
      if (code > 0) {
        if (
          colaboracionSelected.collaborationType ==
          Constants.COLLABORATION_PRINCIPLE
        ) {
          return targetPrincipios?.find(
            (p) => p.code == colaboracionSelected.collaborationTypeCode
          )?.title;
        } else {
          return compromisos?.find(
            (c) => c.code == colaboracionSelected.collaborationTypeCode
          )?.title;
        }
      }
    }
  };

  const handleTrabajarColaboracion = (colaboracionSelected) => {
    setOpenModalTrabajar(true);
    setModeEdit(false);
    const colaboracionConTituloTipo = {
      ...colaboracionSelected,
      tipoDeColaboracionTitulo:
        obtenerTituloDeTipoColaboracionSelected(colaboracionSelected),
    };
    setColaboracionSelected(colaboracionConTituloTipo);
    setComentariosColaboracion(colaboracionSelected.comments);
  };

  const handleFinalizarColaboracion = (colaboracionSelected) => {
    setOpenModalFinalizar(true);
    setModeEdit(false);
    const colaboracionConTituloTipo = {
      ...colaboracionSelected,
      tipoDeColaboracionTitulo:
        obtenerTituloDeTipoColaboracionSelected(colaboracionSelected),
    };
    setColaboracionSelected(colaboracionConTituloTipo);
    setComentariosColaboracion(colaboracionSelected.comments);
  };

  const handleVerReporte = (colaboracionSelected) => {
    setOpenModalReporte(true);
    setModeEdit(false);
    setColaboracionSelected(colaboracionSelected);
  };

  const handleEliminarColaboracion = (colaboracionSelected) => {
    setLoading(true);
    openSpinner();

    Web3Manager.callContract(
      Web3Manager.CONTRACT_COLLABORATIONS,
      "deleteCollaboration",
      colaboracionSelected.code
    )
      .then(async (response) => {
        handleOpenNotificacionTx(Web3Manager.processSuccessResponse(response));

        await onSearch();
      })
      .catch((error) => {
        handleOpenNotificacionError(Web3Manager.processErrorResponse(error));
      })
      .finally(() => {
        closeSpinner();
        setLoading(false);
      });
  };
  
  const handleCloseModalColaboracion = async (
    canceled,
    txResponse,
    isSuccess
  ) => {
    if (!canceled) {
      try {
        if (isSuccess) {
          handleOpenNotificacionTx(
            Web3Manager.processSuccessResponse(txResponse)
          );

          await onSearch();
        } else {
          handleOpenNotificacionError(
            Web3Manager.processErrorResponse(txResponse)
          );
          return;
        }
      } catch (error) {
        handleOpenNotificacionError(error);
        return;
      } finally {
        closeSpinner();
      }
    }

    setOpenModalColaboracion(false);
    setColaboracionSelected(null);
    setModeEdit(false);
  };
  
  const handleCloseModalIniciar = async (canceled, txResponse, isSuccess) => {
    if (!canceled) {
      try {
        if (isSuccess) {
          handleOpenNotificacionTx(
            Web3Manager.processSuccessResponse(txResponse)
          );

          await onSearch();
        } else {
          handleOpenNotificacionError(
            Web3Manager.processErrorResponse(txResponse)
          );
          return;
        }
      } catch (error) {
        console.error(error);
        handleOpenNotificacionError(error);
      } finally {
        closeSpinner();
      }
    }
    setOpenModalIniciar(false);
    setColaboracionSelected(null);
    setModeEdit(false);
  };
  
  const handleCloseModalTrabajar = async (canceled, txResponse, isSuccess) => {
    if (!canceled) {
      try {
        if (isSuccess) {
          handleOpenNotificacionTx(
            Web3Manager.processSuccessResponse(txResponse)
          );

          await onSearch();

          setColaboracionSelected((prev) => {
            if (!prev) return prev;

            const updated = colaboracionesData.find((c) => c.id === prev.id);

            return updated || prev;
          });
          
        } else {
          handleOpenNotificacionError(
            Web3Manager.processErrorResponse(txResponse)
          );
          return; // No se cierra el modal
        }
      } catch (error) {
        console.error(error);
        handleOpenNotificacionError(error);
        return;
      } finally {
        closeSpinner();
      }
    }

    setOpenModalTrabajar(false);
    setColaboracionSelected(null);
    setModeEdit(false);
  };
  
  const handleCloseModalFinalizar = async (canceled, txResponse, isSuccess) => {
    if (!canceled) {
      try {
        if (isSuccess) {
          handleOpenNotificacionTx(
            Web3Manager.processSuccessResponse(txResponse)
          );
          await onSearch();
        } else {
          handleOpenNotificacionError(
            Web3Manager.processErrorResponse(txResponse)
          );
          return;
        }
      } catch (error) {
        console.error(error);
        handleOpenNotificacionError(error);
        return;
      } finally {
        closeSpinner();
      }
    }

    setOpenModalFinalizar(false);
    setColaboracionSelected(null);
    setModeEdit(false);
  };

  const handleCloseModalReporte = async (canceled, txResponse, isSuccess) => {
    if (!canceled) {
      try {
        if (isSuccess) {
          handleOpenNotificacionTx(
            Web3Manager.processSuccessResponse(txResponse)
          );

          await onSearch();
        } else {
          handleOpenNotificacionError(
            Web3Manager.processErrorResponse(txResponse)
          );
          return; // No se cierra el modal
        }
      } catch (error) {
        console.error(error);
        handleOpenNotificacionError(error);
        return;
      } finally {
        closeSpinner();
      }
    }

    setOpenModalReporte(false);
    setColaboracionSelected(null);
    setModeEdit(false);
  };
  
  const getCategoriasColaboracion = () => {
    getCollaborationCategories()
      .then((response) => {
        setCategoriasColaboracion(response);
      })
      .catch((error) => {
        handleOpenNotificacionError(Web3Manager.processErrorResponse(error));
      });
  };
  const getPrincipios = () => {
    getPrinciples()
      .then((response) => {
        const updatedResponse = response.map((item) => ({
          ...item,
          code: item.id,
        }));

        setTargetPrincipios(updatedResponse);
      })
      .catch((error) => {
        handleOpenNotificacionError(Web3Manager.processErrorResponse(error));
      });
  };

  useEffect(() => {
    if (!Web3Manager.isAccountConnected()) {
      Common.goForward("/errorConnection");
      return;
    }

    getCategoriasColaboracion();
    getPrincipios();
    getCompromisosEnabled();

    getSocios(
      Constants.MEMBER_TYPE_SIN_REGISTRO,
      Constants.CATEGORY_ANY,
      "",
      Constants.MEMBER_STATUS_ANY,
      true
    );
  }, []);

  useEffect(() => {
    if (initialized) {
      onSearch();
    }
  }, [initialized]);

  const filterColaborations = (colaboracionesData, pathname) => {
    return colaboracionesData
      .filter((colaboracion) => {
        if (pathname.includes("MisColaboraciones")) {
          return (
            (colaboracion.status !== Constants.COLLABORATION_STATUS_PENDING &&
              colaboracion.targetMemberAddress.toLowerCase() ===
                Web3Manager.MEMBER_DATA.memberAddress.toLowerCase()) ||
            colaboracion.sourceMemberAddress.toLowerCase() ===
              Web3Manager.MEMBER_DATA.memberAddress.toLowerCase()
          );
        } else {
          return (
            colaboracion.status !== Constants.COLLABORATION_STATUS_PENDING ||
            colaboracion.sourceMemberAddress.toLowerCase() ===
              Web3Manager.MEMBER_DATA.memberAddress.toLowerCase()
          );
        }
      })
      .sort((a, b) => b.id - a.id);
  };

  return (
    <Container maxWidth="xl">
      <Box>
        <Typography
          variant="h3"
          color={"#005A59"}
          align="center"
          p={3}
          sx={{
            fontSize: {
              lg: "48px",
              sm: "36px",
              xs: "30px",
            },
          }}
        >
          {pathname.includes("MisColaboraciones")
            ? "Mis Colaboraciones"
            : "Colaboraciones"}
        </Typography>
      </Box>
      <>
        {matches ? (
          <ColaboracionesCard
            loading={loading}
            filtros={filtros}
            setFiltros={setFiltros}
            getColaboraciones={onSearch}
            showSourceAndTargetFilters={!pathname.includes("MisColaboraciones")}
            handleEditarColaboracion={handleEditarColaboracion}
            handleTrabajarColaboracion={handleTrabajarColaboracion}
            handleIniciarColaboracion={handleIniciarColaboracion}
            handleFinalizarColaboracion={handleFinalizarColaboracion}
            handleEliminarColaboracion={handleEliminarColaboracion}
            handleVerReporte={handleVerReporte}
            handleAgregarColaboracion={handleAgregarColaboracion}
            showSpinner={showSpinner}
            filterColaborations={filterColaborations}
            colaboracionesData={colaboracionesData}
            pathname={pathname}
            openModalColaboracion={openModalColaboracion}
            colaboracionSelected={colaboracionSelected}
            handleCloseModalColaboracion={handleCloseModalColaboracion}
            modeEdit={modeEdit}
            socios={socios}
            targetPrincipios={targetPrincipios}
            categoriasColaboracion={categoriasColaboracion}
            compromisos={compromisos}
            setLoading={setLoading}
            openModalIniciar={openModalIniciar}
            openModalTrabajar={openModalTrabajar}
            openModalFinalizar={openModalFinalizar}
            openModalReporte={openModalReporte}
            handleCloseModalIniciar={handleCloseModalIniciar}
            handleCloseModalTrabajar={handleCloseModalTrabajar}
            handleCloseModalFinalizar={handleCloseModalFinalizar}
            handleCloseModalReporte={handleCloseModalReporte}
            comentariosColaboracion={comentariosColaboracion}
          />
        ) : (
          <ColaboracionesTable
            loading={loading}
            filtros={filtros}
            setFiltros={setFiltros}
            getColaboraciones={onSearch}
            showSourceAndTargetFilters={!pathname.includes("MisColaboraciones")}
            handleEditarColaboracion={handleEditarColaboracion}
            handleTrabajarColaboracion={handleTrabajarColaboracion}
            handleIniciarColaboracion={handleIniciarColaboracion}
            handleFinalizarColaboracion={handleFinalizarColaboracion}
            handleEliminarColaboracion={handleEliminarColaboracion}
            handleVerReporte={handleVerReporte}
            handleAgregarColaboracion={handleAgregarColaboracion}
            showSpinner={showSpinner}
            filterColaborations={filterColaborations}
            colaboracionesData={colaboracionesData}
            pathname={pathname}
            openModalColaboracion={openModalColaboracion}
            colaboracionSelected={colaboracionSelected}
            handleCloseModalColaboracion={handleCloseModalColaboracion}
            modeEdit={modeEdit}
            socios={socios}
            targetPrincipios={targetPrincipios}
            categoriasColaboracion={categoriasColaboracion}
            compromisos={compromisos}
            setLoading={setLoading}
            openModalIniciar={openModalIniciar}
            openModalTrabajar={openModalTrabajar}
            openModalFinalizar={openModalFinalizar}
            openModalReporte={openModalReporte}
            handleCloseModalIniciar={handleCloseModalIniciar}
            handleCloseModalTrabajar={handleCloseModalTrabajar}
            handleCloseModalFinalizar={handleCloseModalFinalizar}
            handleCloseModalReporte={handleCloseModalReporte}
            comentariosColaboracion={comentariosColaboracion}
          />
        )}
      </>
    </Container>
  );
};

export default ColaboracionesPage;
